import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../services/api';
import PublicHeader from '../components/PublicHeader';
import PublicFooter from '../components/PublicFooter';

import visaIcon from '../assets/images/payments/visa.png';
import mastercardIcon from '../assets/images/payments/mastercard.png';
import cbIcon from '../assets/images/payments/cb.png';
import applePayIcon from '../assets/images/payments/apple-pay.png';
import googlePayIcon from '../assets/images/payments/google-pay.png';

const paymentMethods = [
  { name: 'Visa', icon: visaIcon, alt: 'Visa' },
  { name: 'Mastercard', icon: mastercardIcon, alt: 'Mastercard' },
  { name: 'CB', icon: cbIcon, alt: 'Carte Bancaire' },
  { name: 'Apple Pay', icon: applePayIcon, alt: 'Apple Pay' },
  { name: 'Google Pay', icon: googlePayIcon, alt: 'Google Pay' },
];

const plansDisplay = [
  { slug: 'free', name: 'Gratuit', price: '0', period: 'toujours', cta: 'Commencer gratuitement', highlight: false },
  { slug: 'monthly', name: 'Mensuel', price: '15', period: '/mois', cta: 'Choisir Mensuel', highlight: true },
  { slug: 'annual', name: 'Annuel', price: '150', period: '/an', cta: 'Choisir Annuel', ctaSub: '(2 mois offerts)', highlight: false },
];

export default function PricingPage() {
  const [plans, setPlans] = useState([]);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const isLoggedIn = !!localStorage.getItem('auth_token');

  useEffect(() => {
    api.get('/plans').then(({ data }) => setPlans(data.data || [])).catch(() => setPlans([]));
  }, []);

  const getPlan = (slug) => plans.find((p) => p.slug === slug) || {};

  const handleCheckout = async (planSlug) => {
    if (!planSlug || planSlug === 'free') return;
    setCheckoutLoading(planSlug);
    try {
      const { data } = await api.post('/checkout', { plan: planSlug });
      if (data.url) window.location.href = data.url;
      else toast.error('URL de paiement indisponible.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors du paiement.');
      setCheckoutLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <PublicHeader />
      <main id="main-content" className="flex-1 max-w-5xl mx-auto w-full py-16 pt-24 px-4" tabIndex={-1}>
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Tarifs simples</h1>
          <p className="text-slate-600 text-lg">Testez gratuitement, passez à l'abonnement quand vous êtes prêt.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {plansDisplay.map((p) => {
            const planData = getPlan(p.slug);
            // Plan annuel : prix à l'année = 15 × 10 mois (2 offerts) = 150€, équivalent 12,50€/mois
            const isAnnual = p.slug === 'annual';
            const priceYearly = isAnnual ? (planData.price_yearly ?? 150) : null;
            const priceMonthly = isAnnual ? (planData.price_monthly ?? 12.5) : (planData.price_monthly ?? (p.slug === 'free' ? 0 : parseFloat(p.price)));
            const price = isAnnual ? priceYearly : priceMonthly;
            const isUnlimited = planData.is_unlimited ?? (p.slug !== 'free');
            return (
              <div
                key={p.slug}
                className={`rounded-2xl border-2 p-8 bg-white ${
                  p.highlight ? 'border-primary-500 shadow-xl md:scale-105' : 'border-slate-200'
                }`}
              >
                {p.highlight && (
                  <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full mb-4">
                    Populaire
                  </span>
                )}
                <h2 className="text-xl font-bold text-slate-900 mb-2">{planData.name || p.name}</h2>
                <div className="mb-4">
                  <span className="text-3xl font-bold text-slate-900">{price} €</span>
                  <span className="text-slate-500 ml-1">{p.period}</span>
                  {isAnnual && (
                    <p className="text-sm text-slate-500 mt-1">
                      soit {priceMonthly} €/mois (2 mois offerts)
                    </p>
                  )}
                </div>
                <ul className="space-y-2 mb-8 text-slate-600">
                  <li>{isUnlimited ? 'Sondages illimités' : '1 sondage gratuit'}</li>
                  <li>Réponses anonymes</li>
                  <li>Rapports & alertes</li>
                  <li>Export PDF / Excel</li>
                </ul>
                {(() => {
                  const btnClass = `block w-full py-3 px-4 text-center font-medium rounded-lg ${
                    p.highlight ? 'bg-primary-600 text-white hover:bg-primary-700' : 'bg-slate-100 text-slate-800 hover:bg-slate-200'
                  }`;
                  const content = p.ctaSub ? (
                    <span className="flex flex-col items-center">
                      <span>{p.cta}</span>
                      <span className="text-xs font-normal opacity-90">{p.ctaSub}</span>
                    </span>
                  ) : (
                    p.cta
                  );
                  if (p.slug === 'free') {
                    return (
                      <Link to="/register" className={btnClass}>
                        {content}
                      </Link>
                    );
                  }
                  if (isLoggedIn) {
                    return (
                      <button
                        type="button"
                        onClick={() => handleCheckout(p.slug)}
                        disabled={checkoutLoading === p.slug}
                        className={`${btnClass} disabled:opacity-50`}
                      >
                        {checkoutLoading === p.slug ? 'Redirection...' : content}
                      </button>
                    );
                  }
                  return (
                    <Link to={`/register?plan=${p.slug}`} className={btnClass}>
                      {content}
                    </Link>
                  );
                })()}
              </div>
            );
          })}
        </div>

        {/* Bloc de confiance pour rassurer le client */}
        <section className="mt-16 pt-12 border-t border-slate-200" aria-label="Sécurité et moyens de paiement">
          <div className="flex flex-col items-center gap-8">
            {/* Moyens de paiement acceptés */}
            <div className="flex items-center justify-center gap-4 flex-wrap" role="img" aria-label="Visa, Mastercard, Carte Bancaire, Apple Pay, Google Pay acceptés">
              {paymentMethods.map(({ name, icon, alt }) => (
                <img
                  key={name}
                  src={icon}
                  alt={alt}
                  className="h-8 w-auto object-contain opacity-90 hover:opacity-100 transition-opacity"
                />
              ))}
            </div>

            {/* Message rassurant */}
            <div className="text-center space-y-2 max-w-xl">
              <p className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-50 text-primary-700 font-semibold">
                <svg className="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                Paiement 100% sécurisé par Stripe
              </p>
              <p className="text-slate-500 text-sm leading-relaxed">
                Vos données bancaires sont cryptées et ne transitent jamais par nos serveurs.
                Annulation à tout moment, sans engagement. Facturation transparente.
              </p>
            </div>

            {/* Garanties rapides */}
            <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-600">
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5 text-success-600 shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Données cryptées SSL
              </span>
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5 text-success-600 shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Annulation en 1 clic
              </span>
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5 text-success-600 shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Sans engagement
              </span>
            </div>
          </div>
        </section>
      </main>
      <PublicFooter />
    </div>
  );
}
